import { Icon } from '@chakra-ui/icon'
import { Flex, Text } from '@chakra-ui/layout'
import { Popover, PopoverAnchor } from '@chakra-ui/popover'
import { Portal } from '@chakra-ui/portal'
import { Box, Button, useBoolean, useOutsideClick } from '@chakra-ui/react'
import React, { ReactNode } from 'react'
import IconCallStop from '~/assets/svgs/icon-call-stop.svg?react'
import IconCall from '~/assets/svgs/icon-call.svg?react'
import IconIncomingReject from '~/assets/svgs/icon-incoming-reject.svg?react'
import IconPhoneHoldHeavy from '~/assets/svgs/icon-phone-hold-heavy.svg?react'
import IconPhoneParkHeavy from '~/assets/svgs/icon-phone-park-heavy.svg?react'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import {
  Menu,
  MenuPlacement,
  MenuPopoverWrapper,
  MenuPositioningWrapper,
} from '~/components/shared/Dropdown/Menu'
import { MangoButton, MangoButtonProps } from '~/components/shared/MangoButton'
import { PresenceState } from '~/utils/xml'

import { useCallIconStyle } from './componentStyles/CallIcon.style'
import {
  IncomingCallButtonVariants,
  useIncomingCallButtonStyles,
} from './componentStyles/IncomingCallButton.style'
import {
  ParkCallMenuStyleValues,
  useParkCallMenuStyle,
} from './componentStyles/ParkCallMenu.style'
import {
  ParkCallMenuItemVariants,
  useParkCallMenuItemStyle,
} from './componentStyles/ParkCallMenuItem.style'

interface CallIconProps {
  icon: SVGComponent
  variant: IncomingCallButtonVariants
}
const CallIcon = ({ icon, variant }: CallIconProps) => {
  const styles = useCallIconStyle({ variant })
  return (
    <Flex sx={styles.container}>
      <Icon as={icon} sx={styles.icon} />
    </Flex>
  )
}

const RejectCall = () => {
  return (
    <CallIcon
      icon={IconIncomingReject}
      variant={IncomingCallButtonVariants.Reject}
    />
  )
}

const AcceptCall = () => {
  return (
    <CallIcon icon={IconCall} variant={IncomingCallButtonVariants.Accept} />
  )
}

const AcceptCallWithOverlap = () => {
  return (
    <CallIcon
      icon={IconCall}
      variant={IncomingCallButtonVariants.AcceptWithOverlap}
    />
  )
}

const EndAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={IconCallStop}
        variant={IncomingCallButtonVariants.EndAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

const HoldAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={IconPhoneHoldHeavy}
        variant={IncomingCallButtonVariants.HoldAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

const ParkAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={IconPhoneParkHeavy}
        variant={IncomingCallButtonVariants.ParkAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

// Placeholder until we have a good disabled state
const IncomingCallButtonDummy = () => {
  return <Box flexShrink="0" boxSize="8.75rem" />
}

type IncomingCallButtonProps = {
  variant: IncomingCallButtonVariants
  children: ReactNode
  onClick: () => void
  isActive?: boolean
  ref?: React.Ref<HTMLButtonElement>
}
export const IncomingCallButton = ({
  variant,
  children,
  onClick,
  isActive,
  ref,
}: IncomingCallButtonProps) => {
  let ButtonIcon: () => React.JSX.Element

  switch (variant) {
    case IncomingCallButtonVariants.Reject:
      ButtonIcon = RejectCall
      break
    case IncomingCallButtonVariants.Accept:
      ButtonIcon = AcceptCall
      break
    case IncomingCallButtonVariants.EndAndAccept:
      ButtonIcon = EndAcceptCall
      break
    case IncomingCallButtonVariants.HoldAndAccept:
      ButtonIcon = HoldAcceptCall
      break
    case IncomingCallButtonVariants.ParkAndAccept:
      ButtonIcon = ParkAcceptCall
      break
    case IncomingCallButtonVariants.AcceptWithOverlap:
      ButtonIcon = AcceptCallWithOverlap
  }

  const styles = useIncomingCallButtonStyles({ variant })

  return (
    <Button ref={ref} sx={styles.button} onClick={onClick} isActive={isActive}>
      <ButtonIcon />
      <Text sx={styles.text}>{children}</Text>
    </Button>
  )
}

interface ParkCallMenuItemProps {
  parkingLot: TrackedParkingLot
  onClick: (parkingLot: TrackedParkingLot) => void
}
const ParkCallMenuItem = ({ parkingLot, onClick }: ParkCallMenuItemProps) => {
  let circleBadgeVariant: CircleBadgeVariant
  let itemVariant: ParkCallMenuItemVariants
  switch (parkingLot.presence) {
    case PresenceState.Available:
      circleBadgeVariant = CircleBadgeVariant.Green
      itemVariant = ParkCallMenuItemVariants.Available
      break
    case PresenceState.Unavailable:
      circleBadgeVariant = CircleBadgeVariant.Red
      itemVariant = ParkCallMenuItemVariants.Unavailable
      break
    default:
      circleBadgeVariant = CircleBadgeVariant.Gray
      itemVariant = ParkCallMenuItemVariants.Available
      break
  }

  const handleClick = () => {
    if (parkingLot.presence === PresenceState.Unavailable) return
    onClick(parkingLot)
  }

  const styles = useParkCallMenuItemStyle({ variant: itemVariant })
  return (
    <Flex sx={styles.container} onClick={handleClick}>
      <CircleBadge variant={circleBadgeVariant} />
      <Text sx={styles.text}>{parkingLot.label}</Text>
    </Flex>
  )
}

interface ParkCallMenuProps {
  anchorRef?: React.RefObject<HTMLDivElement>
  parkingLots: TrackedParkingLot[]
  onClickParkingLot: (parkingLot: TrackedParkingLot) => void
  isInPopup?: boolean
}
const ParkCallMenu = function ParkCallMenu({
  anchorRef,
  parkingLots,
  onClickParkingLot,
  isInPopup = false,
}: ParkCallMenuProps) {
  const styles = useParkCallMenuStyle()

  const menuItems = parkingLots.map((parkingLot) => {
    return {
      value: parkingLot.id,
      content: (
        <ParkCallMenuItem
          key={parkingLot.id}
          parkingLot={parkingLot}
          onClick={onClickParkingLot}
        />
      ),
    }
  })
  menuItems.unshift({
    value: 0,
    content: (
      <Flex sx={styles.headerContainer}>
        <Text sx={styles.headerText}>Select a parking spot</Text>
      </Flex>
    ),
  })

  return (
    <Menu
      anchorRef={anchorRef}
      items={menuItems}
      options={{
        maxHeight: isInPopup ? '70vh' : 'inherit',
        width: isInPopup
          ? ParkCallMenuStyleValues.containerWidthPopup
          : ParkCallMenuStyleValues.containerWidth,
        placement: MenuPlacement.RightTop,
      }}
    />
  )
}

type ParkCallButtonProps = {
  parkingLots: TrackedParkingLot[]
  onClickParkingLot: (parkingLot: TrackedParkingLot) => void
  isInPopup?: boolean
  mangoButtonProps?: MangoButtonProps
} & IncomingCallButtonProps
export const ParkCallButton = ({
  parkingLots,
  onClickParkingLot,
  isInPopup = false,
  mangoButtonProps = {},
  ...rest
}: ParkCallButtonProps) => {
  const [menuOpenState, setMenuOpenState] = useBoolean()
  const boxRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  useOutsideClick({
    ref: boxRef,
    handler: () => {
      setMenuOpenState.off()
    },
  })

  const handleButtonClick = () => {
    setMenuOpenState.toggle()
  }

  if (parkingLots.length === 0) {
    // UX: Could replace with a 'disabled' style button instead
    return <IncomingCallButtonDummy />
  }

  return (
    <>
      {isInPopup && (
        <Popover isOpen={menuOpenState} placement="auto">
          <PopoverAnchor>
            <MangoButton
              ref={buttonRef}
              flexShrink={1}
              leftIcon={IconPhoneParkHeavy}
              variant={MangoButtonVariant.Success_300}
              onClick={handleButtonClick}
              {...mangoButtonProps}
            >
              Park & Accept
            </MangoButton>
          </PopoverAnchor>
          <Portal>
            {menuOpenState && (
              <MenuPopoverWrapper ref={boxRef}>
                <ParkCallMenu
                  isInPopup={isInPopup}
                  parkingLots={parkingLots}
                  onClickParkingLot={onClickParkingLot}
                />
              </MenuPopoverWrapper>
            )}
          </Portal>
        </Popover>
      )}

      {!isInPopup && (
        <MenuPositioningWrapper ref={boxRef}>
          <IncomingCallButton
            {...rest}
            onClick={handleButtonClick}
            isActive={menuOpenState}
          />
          {menuOpenState && (
            <ParkCallMenu
              parkingLots={parkingLots}
              onClickParkingLot={onClickParkingLot}
            />
          )}
        </MenuPositioningWrapper>
      )}
    </>
  )
}
